import {
  useQuery,
  useMutation,
  useQueryClient,
  type UseQueryResult,
  type UseMutationResult,
} from '@tanstack/react-query'
import { pickupsApi } from '@/lib/api'
import { saveCachedPickups, removeCachedPickup } from '@/lib/db'
import type { Pickup, PickupFormData } from '@/types'

export const pickupKeys = {
  all: ['pickups'] as const,
  lists: () => [...pickupKeys.all, 'list'] as const,
  list: (params?: { month?: string; date_from?: string; date_to?: string; client_id?: number; status?: string }) =>
    [...pickupKeys.lists(), params] as const,
  detail: (id: number) => [...pickupKeys.all, 'detail', id] as const,
}

export function usePickups(params?: {
  month?: string
  date_from?: string
  date_to?: string
  client_id?: number
  status?: string
}): UseQueryResult<Pickup[]> {
  return useQuery({
    queryKey: pickupKeys.list(params),
    queryFn: async () => {
      const res = await pickupsApi.list(params)
      await saveCachedPickups(
        res.data,
        params?.date_from && params?.date_to
          ? { dateFrom: params.date_from, dateTo: params.date_to, status: params.status }
          : undefined
      )
      return res.data
    },
  })
}

export function usePickup(id: number): UseQueryResult<Pickup> {
  return useQuery({
    queryKey: pickupKeys.detail(id),
    queryFn: async () => {
      const res = await pickupsApi.get(id)
      await saveCachedPickups([res.data])
      return res.data
    },
    enabled: Boolean(id),
  })
}

export function useCreatePickup(): UseMutationResult<
  Pickup,
  Error,
  PickupFormData
> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (data: PickupFormData) => {
      const res = await pickupsApi.create(data)
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: pickupKeys.lists() })
    },
  })
}

export function useUpdatePickup(): UseMutationResult<
  Pickup,
  Error,
  { id: number; data: Partial<PickupFormData> }
> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, data }) => {
      const res = await pickupsApi.update(id, data)
      return res.data
    },
    onSuccess: (updated) => {
      queryClient.invalidateQueries({ queryKey: pickupKeys.lists() })
      queryClient.setQueryData(pickupKeys.detail(updated.id), updated)
    },
  })
}

export function useDeletePickup(): UseMutationResult<void, Error, number> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: number) => {
      await pickupsApi.delete(id)
    },
    onSuccess: async (_, id) => {
      await queryClient.invalidateQueries({ queryKey: pickupKeys.lists() })
      queryClient.removeQueries({ queryKey: pickupKeys.detail(id) })
      await removeCachedPickup(id)
    },
  })
}
